import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box, Button, Container, TextField, Typography, Paper, Snackbar, Alert, Stack
} from '@mui/material';
import BreadCrumbs from '../components/Breadcrumbs';
import { updateServerPluginFile, getServerPluginEdit } from '../http/ServerAPI';
import Editor from '@monaco-editor/react';
import { observer } from 'mobx-react-lite';

const STORAGE_KEY = (server_id, id, addition_id) => `pluginEdit_${server_id}_${id}_${addition_id}`;

const ServerPluginEdit = observer(() => {
    const { server_id, addition_id, id } = useParams();
    const editorRef = useRef(null);

    const [code, setCode] = useState('');
    const [fileName, setFileName] = useState('');
    const [readOnly, setReadOnly] = useState(false);
    const [hasErrors, setHasErrors] = useState(false);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const hrefs = [{ href: `/server/plugins/${server_id}`, name: 'Список плагинов' }];

    const getExtension = (name) => name ? name.split('.').pop().toLowerCase() : '';
    const isEditableExtension = (ext) => ['cs', 'json'].includes(ext);
    const getLanguage = () => {
        const ext = getExtension(fileName);
        if (ext === 'cs') return 'csharp';
        if (ext === 'json') return 'json';
        return 'plaintext';
    };

    // Загрузка при монтировании
    useEffect(() => {
        const local = localStorage.getItem(STORAGE_KEY(server_id, id, addition_id));
        if (local) {
            const saved = JSON.parse(local);
            setFileName(saved.fileName);
            setCode(saved.code);
            return;
        }

        getServerPluginEdit(+server_id, +id, +addition_id)
            .then(data => {
                let content = data.code || '';
                const ext = data.ext || 'json';
                setFileName(data.filename);
                if (ext === 'json') {
                    try {
                        const json = JSON.parse(content);
                        content = JSON.stringify(json, null, 2);
                    } catch {
                        console.warn('Невалидный JSON, пропущено форматирование');
                    }
                }
                setCode(content);
            })
            .catch(console.error);
    }, [id, addition_id]);

    // Сохранение в localStorage
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY(server_id, id, addition_id), JSON.stringify({ code, fileName }));
    }, [code, fileName]);

    const handleEditorDidMount = (editor, monaco) => {
        editorRef.current = editor;

        monaco.editor.onDidChangeMarkers(() => {
            const model = editor.getModel();
            if (!model) return;
            const markers = monaco.editor.getModelMarkers({ resource: model.uri });
            setHasErrors(markers.length > 0);
        });

        editor.onDidChangeModelContent(() => {
            const model = editor.getModel();
            if (!model) return;
            setCanUndo(editorRef.current.getModel().canUndo());
            setCanRedo(editorRef.current.getModel().canRedo());
        });
    };

    const handleSave = async () => {
        try {
            await updateServerPluginFile(+server_id, +id, +addition_id, code);
            setSnackbar({ open: true, message: 'Файл успешно сохранён', severity: 'success' });
            localStorage.removeItem(STORAGE_KEY(server_id, id, addition_id)); // очищаем черновик
        } catch (e) {
            console.error(e);
            setSnackbar({ open: true, message: 'Ошибка при сохранении', severity: 'error' });
        }
    };

    const handleUndo = () => {
        editorRef.current.trigger('keyboard', 'undo', null);
    };

    const handleRedo = () => {
        editorRef.current.trigger('keyboard', 'redo', null);
    };

    return (
        <Container maxWidth="xl">
            <Box sx={{ width: '100%', mt: 3, mb: 3 }}>
                <BreadCrumbs hrefs={hrefs} name={`Конфиг плагина: ${fileName}`} />
            </Box>

            <Typography variant="h5" gutterBottom>
                Редактирование файла {fileName || ''}
            </Typography>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Содержимое файла</Typography>
                <Editor
                    height="600px"
                    language={getLanguage()}
                    theme="vs-dark"
                    value={code}
                    onChange={(value) => setCode(value || '')}
                    onMount={handleEditorDidMount}
                    options={{
                        fontSize: 14,
                        minimap: { enabled: false },
                        readOnly,
                        scrollBeyondLastLine: false,
                    }}
                />
                {hasErrors && (
                    <Typography sx={{ mt: 1 }} variant="body2" color="error">
                        В коде есть синтаксические ошибки. Пожалуйста, исправьте их перед сохранением.
                    </Typography>
                )}
                {!isEditableExtension(getExtension(fileName)) && (
                    <Typography sx={{ mt: 1 }} variant="body2" color="error">
                        Файл не поддерживается для редактирования. Доступны только .cs и .json
                    </Typography>
                )}
            </Paper>

            <Box textAlign="right">
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button variant="outlined" onClick={handleUndo} disabled={!canUndo}>
                        Отменить
                    </Button>
                    <Button variant="outlined" onClick={handleRedo} disabled={!canRedo}>
                        Повторить
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={readOnly || hasErrors}
                    >
                        Сохранить
                    </Button>
                </Stack>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
});

export default ServerPluginEdit;
